/**
 * @file 质押结果弹窗
 */
import { useEffect, useMemo } from 'react';
import { Modal, Button } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { CoinInfo } from '@/types/common';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { useParams } from '@web3/base-hooks';
import { GasStore } from '@/types/stake';
import { useOrderStatus } from '@/hooks/order';
import { StakingContent } from '../stakingContent';
import { PendingArr, SuccessArr, FailArr } from '@/config';
import { useRefreshContext } from '@/hooks/useRefresh';
import { useCurrentChainAddress } from '@/hooks/wallet';
import {
  OrderResultPending,
  OrderResultSuccess,
  OrderResultFail,
  Row,
  SlippageLostRow,
} from '@/components';
import BigNumber from 'bignumber.js';
import cls from 'classnames';
import { formatUSDYield } from '@/utils/num';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { appPath } from '@web3/base-utils';
import styles from './index.module.scss';

interface Props {
  show: boolean;
  currentStakeToken: CoinInfo;
  stakeAmount: string;
  slippage: string;
  stakeLpInfo: Partial<CoinInfo>;
  gasStore: GasStore;
  aprPerDay: string;
  orderCode: string;
}

export const OrderResultModal = (props: Props) => {
  const {
    show,
    currentStakeToken,
    stakeAmount,
    stakeLpInfo,
    gasStore,
    aprPerDay,
    slippage,
    orderCode,
  } = props;
  const { poolInfo } = usePoolInfoContext();
  const fm = useFm();
  const { locale } = useParams();
  const { refresh } = useRefreshContext();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);

  const onConfirm = () => {
    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPagestakeresult',
      button_name: 'check_order_detail',
    });

    if (window.top) {
      window.top.location.href = `/${locale}${appPath}/history?chainId=${poolInfo.chainId}&poolId=${poolInfo.id}`;
    }
  };

  const { orderStatusData } = useOrderStatus(orderCode);

  useEffect(() => {
    const params = {
      page_name: 'StakingWeb_bbSOLPagestakeresult',
      orderresultName: 'pending',
    };

    if (SuccessArr.includes(orderStatusData.status)) {
      params.orderresultName = 'succeed';
    }

    if (FailArr.includes(orderStatusData.status)) {
      params.orderresultName = 'fail';
    }

    scTrack(Event.Web3PageView, params);
  }, [orderStatusData]);

  const useHexPrefix = useMemo(() => {
    if (poolInfo?.chainCode === 'SOL') return false;
    return true;
  }, [poolInfo]);

  return (
    <Modal
      open={show}
      onOk={onConfirm}
      onCancel={() => {
        refresh();
        scTrack(Event.Web3UserClick, {
          page_name: 'StakingWeb_bbSOLPagestakeresult',
          button_name: 'cancel',
        });
      }}
      width={480}
      footer={null}
      modal={false}
      title={fm('please_confirm')}
    >
      {/* 质押过程中 */}
      {PendingArr.includes(orderStatusData.status) && (
        <OrderResultPending finishTime={orderStatusData.finishTime}>
          <>
            <div className={styles.pendingContent}>
              <StakingContent
                currentStakeToken={currentStakeToken}
                stakeAmount={stakeAmount}
                stakeLpInfo={stakeLpInfo}
              />
            </div>
            <Row
              label={fm('estimate_daily_profit')}
              value={`$${BigNumber(aprPerDay).toFixed(6)}`}
            />
            <SlippageLostRow
              show={!!slippage}
              className="mt-3"
              slippage={slippage}
              isWarn={poolInfo.isHaveSwap}
            />
            <Row
              label={fm('estimated_cost')}
              className={cls('mt-3', styles.cost)}
              value={
                <div>
                  <div
                    className={styles.amount}
                  >{`${gasStore.amount} ${gasStore.symbol}`}</div>
                  <div className={styles.price}>
                    {`${formatUSDYield(gasStore.price)}`}
                  </div>
                </div>
              }
            />
          </>
        </OrderResultPending>
      )}
      {/* 质押成功 */}
      {SuccessArr.includes(orderStatusData.status) && (
        <OrderResultSuccess
          data={[
            {
              label: fm('fromWalletAddress'),
              address: walletAddress,
              useHexPrefix,
            },
            {
              label: fm('toContractAddress'),
              address: poolInfo.contractAddress,
              useHexPrefix,
            },
            {
              label: fm('transactionHistory'),
              address: orderStatusData.hash,
              useHexPrefix,
            },
          ]}
        >
          <>
            <div className={styles.row}>
              <div
                className={styles.successDesc}
              >{`${fm('pledgeSuccess')} ${stakeAmount}`}</div>

              <img
                src={currentStakeToken.iconNightUrl}
                className={styles.successIcon}
              />
              <div className={styles.successSymbol}>
                {currentStakeToken.symbol}
              </div>
            </div>
            <div
              className={cls(styles.successDesc, styles.row)}
            >{`${fm('chainExecutionDelayNotice')}`}</div>
          </>
        </OrderResultSuccess>
      )}
      {/* 质押失败 */}
      {FailArr.includes(orderStatusData.status) && (
        <OrderResultFail errorText={fm('operationNetworkIssueRetry')} />
      )}
      <Button className={styles.button} variant="primary" onClick={onConfirm}>
        {fm('ido_view_explorer')}
      </Button>
    </Modal>
  );
};
